import create from 'zustand';

import { ReduxDevTools } from '../helpers/storeLogger';
import { KonakartService } from '../services/konakart';
import { sessionStorage } from '../helpers/sessionStorage';
import { T_Coupon, T_CTOrderItem, T_Order, T_OrderProduct, T_OrderRequest, T_Product } from '../types';
import { categoryIds, CreditCampaignCodes, productSkuIds } from '../config';
import { boxesStoreApi, BoxTypes } from './boxesStore';

/**
 * Manages the Order portion of Konakart.
 * Can load, updateOrder and clear the order
 */
interface OrderStore {
  initializedOrderStore: boolean;
  isOrderPending?: boolean;

  order?: T_Order;
  isToggleProduct?: boolean;

  orderRequest?: T_OrderRequest[];

  loadOrderStore: (kkService: KonakartService, customerId: string) => Promise<void>;

  /** Account number for the current customer — used by CT calls. */
  accountNumber: string;

  /** Stores account number once so updateOrder / clearOrder don't need it repeatedly. */
  setAccountNumber: (accountNumber?: string) => void;

  /** Directly stores the order returned by CT getCart (skips API call). */
  setOrderFromGetCart: (order: T_Order, products: T_OrderRequest[]) => void;

  /** Last non-subscribed products successfully synced to CT. */
  previousCTProducts: T_OrderRequest[];

  updateOrder: (
    kkService: KonakartService,
    customerId: string,
    products: T_OrderRequest[],
    coupons?: T_Coupon[],
    billingCoupons?: string[],
    ctCoupons?: T_Coupon[],
    productsFromStore?: T_Product[]
  ) => Promise<void>;

  updateOrderforAbandoned: (cartItems: any, coupons?: T_Coupon[]) => void;

  clearOrder: (kkService: KonakartService, customerId: string) => Promise<void>;
}

const isAcquisition = ['get.dev.sky.co.nz', 'get.sky.co.nz', 'get.prelive.sky.co.nz', 'localhost:3100'].some(el =>
  window?.location?.host.includes(el)
);

const quizJourney = window?.location?.search.includes('sid') ? true : false;

const localStorageStateName = 'Order Store';

/**
 * Reads persisted orderRequest from sessionStorage (CT sessions) or localStorage.
 */
const getOrderRequest = (): any[] => {
  try {
    // if (isCTEnabled()) {
    return JSON.parse(sessionStorage.getItem(localStorageStateName) ?? '[]');
    // }

    // const session = sessionStorage.getItem(localStorageStateName);

    // if (session && session !== '[]') {
    //   return JSON.parse(session);
    // }

    // return JSON.parse(localStorage.getItem(localStorageStateName) ?? '[]');
  } catch {
    return [];
  }
};

/**
 * Persists orderRequest to sessionStorage when CT is enabled, localStorage otherwise.
 */
const setOrderRequest = (value: T_OrderRequest[]) => {
  // const storage = isCTEnabled() ? sessionStorage : localStorage;
  const storage = sessionStorage;

  storage.setItem(localStorageStateName, JSON.stringify(value));
};

/**
 * Removes hidden Sky TV discount products from UI cart.
 */
const filterDiscountProducts = (order: T_Order) => {
  const filteredProducts = order?.orderProducts?.filter(
    product =>
      product.product.sku !== productSkuIds.broadbandStarterDiscountOneGig.primary &&
      product.product.sku !== productSkuIds.broadbandStarterDiscountHundredMeg.primary
  );

  return {
    ...order,
    orderProducts: filteredProducts,
  };
};

const getOrderRequestMatchKey = (item: Pick<T_OrderRequest, 'productId' | 'occurrenceNumber'>) => {
  const occurrenceNumber = item.occurrenceNumber ?? null;
  if (!occurrenceNumber) return { key: `${item.productId}_${null}`, skus: [] };

  const upgradedBox = boxesStoreApi.getState().boxes.find(box => {
    return box.boxType === BoxTypes.UPGRADE && box.id === occurrenceNumber && !!box.upgradeFrom?.occurrenceNumber;
  });

  const sourceOccurrenceNumber = upgradedBox?.upgradeFrom?.occurrenceNumber ?? null;
  const sourceBox = boxesStoreApi
    .getState()
    .boxes.find(box => box.boxType === BoxTypes.SUBSCRIBED && box.id === sourceOccurrenceNumber);

  const skus = sourceBox?.products?.map(product => product.sku).filter((sku): sku is string => Boolean(sku)) ?? [];
  const mainProduct = sourceBox?.products?.find(product => product.categoryId === categoryIds.box);

  return {
    key: `${mainProduct?.id}_${sourceOccurrenceNumber}`,
    skus,
  };
};

const [useOrderStore, orderStoreApi] = create<OrderStore>(
  ReduxDevTools(
    (set, get) => ({
      initializedOrderStore: false,
      isOrderPending: true,
      products: [],
      orderRequest: isAcquisition ? [] : getOrderRequest(),
      isToggleProduct: false,
      previousCTProducts: [],
      accountNumber: '',

      setAccountNumber: accountNumber => {
        set({ accountNumber: accountNumber ?? '' }, 'set order account number');
      },

      loadOrderStore: async (kkService, customerId) => {
        if (!customerId) {
          set({ initializedOrderStore: false }, 'customer id not initialized');
          throw new Error('Customer id not initialized');
        }
        const accountNumber = get().accountNumber || undefined;
        let onLoadOrder = await kkService.getOrder(customerId, 'web', accountNumber);
        console.log('loadOrderStore', onLoadOrder);

        let setOccurenceProducts;

        // WEB-2989 to fix the Primary box render issue in ice-web
        if (getOrderRequest().length > 0 && onLoadOrder.orderProducts.length > 0 && !quizJourney) {
          setOccurenceProducts = getOrderRequest()
            .map((order: { occurrenceNumber: any; productId: any }) => {
              const orderKK = onLoadOrder.orderProducts.find(ordereleKK => ordereleKK.productId === order.productId);

              if (orderKK) {
                return {
                  ...orderKK,
                  occurrenceNumber: order.occurrenceNumber!,
                };
              }

              return undefined;
            })
            .filter(Boolean);

          onLoadOrder.orderProducts = (setOccurenceProducts
            ? setOccurenceProducts
            : onLoadOrder.orderProducts) as T_OrderProduct[];
        }

        set(
          {
            initializedOrderStore: true,
            order: onLoadOrder,
            orderRequest: getOrderRequest(),
          },
          'initialized Order Store'
        );
      },

      updateOrder: async (
        kkService,
        customerId,
        products,
        coupons,
        billingCoupons,
        ctCoupons = [],
        productsFromStore = []
      ) => {
        const accountNumber = get().accountNumber || undefined;
        const couponCodes = coupons ? coupons.map(cp => cp.couponCode) : [];
        // only for handling bb speed upgrade offer for multiple coupons
        const billingCouponCodes = billingCoupons ? billingCoupons.map(cp => cp) : [];
        // if (products.length === 0 && couponCodes.length > 0 && !isCTEnabled()) {
        //   const updatedOrder = await kkService.updateOrder(customerId, []);
        //   set(
        //     {
        //       order: updatedOrder,
        //       orderRequest: getOrderRequest(),
        //     },
        //     'clear order'
        //   );

        //   return;
        // }

        if (typeof window !== 'undefined') {
          window.document.body.style.cursor = 'progress';
        }
        let updatedOrder;
        try {
          const ctCouponCodes: string[] = ctCoupons.map(coupon => coupon.couponCode);
          const allApplyedCouponCodes: string[] = Array.from(
            new Set([...ctCouponCodes, ...couponCodes, ...billingCouponCodes])
          );

          /**
           * CT FLOW
           * Only send delta changes (add/remove items).
           * Existing subscribed products already exist in CT cart.
           */
          const previousReqs = get().previousCTProducts.filter(p => !p.currentlySubscribed);
          const currentNonSubscribed = products.filter(p => !p.currentlySubscribed);

          /**
           * Optimized lookup maps
           */
          const productMap = new Map(productsFromStore.map(p => [p.id, p.sku]));
          const previousMap = new Map(previousReqs.map(p => [`${p.productId}_${p.occurrenceNumber ?? null}`, p]));
          const currentMap = new Map(products.map(p => [`${p.productId}_${p.occurrenceNumber ?? null}`, p]));

          const codes: T_CTOrderItem[] = [];
          const upgradedCodes = new Set<string>();
          /**
           * ADD PRODUCTS / HANDLE UPGRADES
           */
          for (const req of currentNonSubscribed) {
            let productSkus = req.sku ?? productMap.get(req.productId);
            // Ensure product exists and has SKU before proceeding.
            if (!productSkus) continue;
            const prev = previousMap.get(`${req.productId}_${req.occurrenceNumber ?? null}`);
            const prevQty = prev?.quantity ?? 0;

            if (req.quantity > prevQty) {
              codes.push({
                code: productSkus,
                quantity: req.quantity - prevQty,
                action: 1,
                occurrenceNumber: req.occurrenceNumber ?? null,
              });

              let isBoxProduct = productsFromStore.find(p => p.sku === productSkus)?.categoryId === categoryIds.box;
              if (isBoxProduct) {
                const { key, skus: upgradeSkus } = getOrderRequestMatchKey(req);
                const curr = currentMap.get(key);

                // REMOVE old products when this is an upgrade
                for (const sku of upgradeSkus) {
                  const occurrenceNumber = curr?.occurrenceNumber ?? null;
                  const key = `${sku}_${occurrenceNumber}`;

                  if (!upgradedCodes.has(key)) {
                    codes.push({
                      code: sku,
                      quantity: 0,
                      action: 0,
                      occurrenceNumber,
                    });

                    upgradedCodes.add(key);
                  }
                }
              }
            }
          }

          /**
           * REMOVE PRODUCTS
           */
          for (const prev of previousReqs) {
            let productSkus = prev.sku ?? productMap.get(prev.productId);
            // Ensure product exists and has SKU before proceeding.
            if (!productSkus) continue;
            const curr = currentMap.get(`${prev.productId}_${prev.occurrenceNumber ?? null}`);
            const currQty = curr?.quantity ?? 0;

            if (currQty < (prev.quantity ?? 0)) {
              codes.push({
                code: productSkus,
                quantity: (prev.quantity ?? 0) - currQty,
                action: 0,
                occurrenceNumber: prev.occurrenceNumber ?? null,
              });
            }
          }

          console.log('updateOrder CT delta', {
            previousReqs,
            currentNonSubscribed,
            products,
            allApplyedCouponCodes,
            ctCoupons,
            couponCodes,
            codes,
          });

          updatedOrder = await kkService.updateOrder(
            customerId,
            codes,
            [...allApplyedCouponCodes],
            undefined,
            undefined,
            accountNumber
          );

          /**
           * Save latest CT products for next delta comparison.
           */
          set({
            previousCTProducts: currentNonSubscribed,
          });

          updatedOrder = filterDiscountProducts(updatedOrder);
        } catch (error) {
          console.error('[orderStore] updateOrder failed:', error);
        } finally {
          set({ isOrderPending: false }, 'pending order check');
        }

        if (typeof window !== 'undefined') {
          window.document.body.style.cursor = 'default';
        }

        setOrderRequest(products);

        /**
         * BB speed upgrade discount adjustment.
         * KK service does not correctly handle multiple coupons.
         */
        let isProduct = false;

        const bbCampaignCodes = Object.values(CreditCampaignCodes.BROADBAND);

        const isBBCouponCode = billingCouponCodes?.some(coupon =>
          bbCampaignCodes.some(value => coupon.includes(value))
        );

        if (isBBCouponCode || get().isToggleProduct) {
          const { BB_FP_BOOST_SPEED_UPGRADE, BB_FP_SPEED_UPGRADE } = CreditCampaignCodes.BB_SPEED_UPGRADE;

          let creditOfferPrice = 0;

          const has09074BB = couponCodes.includes(BB_FP_SPEED_UPGRADE);

          const has09075BB = couponCodes.includes(BB_FP_BOOST_SPEED_UPGRADE);

          if (has09074BB || has09075BB) {
            creditOfferPrice = has09074BB ? 10 : has09075BB ? 20 : 0;

            updatedOrder?.orderTotals?.forEach(orderTotal => {
              if (orderTotal.className === 'ot_total_no_otc' || orderTotal.className === 'ot_total') {
                orderTotal.value = orderTotal.value - creditOfferPrice;
              }
            });

            isProduct = true;
          }
        }

        set(
          {
            order: updatedOrder,
            orderRequest: products,
            initializedOrderStore: true,
            isToggleProduct: isProduct,
          },
          'update Order'
        );
      },

      setOrderFromGetCart: (order, products) => {
        setOrderRequest(products);

        const filteredOrder = filterDiscountProducts(order);

        set(
          {
            order: filteredOrder,
            initializedOrderStore: true,
            isOrderPending: false,
          },
          'set order from createCart'
        );
      },

      updateOrderforAbandoned: (cartItems, coupons) => {
        const couponCodes = coupons ? coupons.map(cp => cp.couponCode) : [];

        if (typeof window !== 'undefined') {
          window.document.body.style.cursor = 'progress';
        }

        let updatedOrder;

        try {
          updatedOrder = cartItems;
        } finally {
          set({ isOrderPending: false }, 'pending order check');
        }

        if (typeof window !== 'undefined') {
          window.document.body.style.cursor = 'default';
        }

        set(
          {
            order: updatedOrder,
            initializedOrderStore: true,
          },
          'update Order'
        );
      },

      /**
       * Clears CT / KK cart.
       */
      clearOrder: async (kkService, customerId) => {
        if (!customerId) return;
        set({ previousCTProducts: [] }, 'clear CT order');

        const updateOrder = get().updateOrder;
        setOrderRequest([]);
        await updateOrder(kkService, customerId, []);
      },
    }),
    'Order Store'
  )
);

export { useOrderStore, orderStoreApi, setOrderRequest };
